import {
  AudioOutlined,
  CommentOutlined,
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { VoiceOrderItemOut } from "../../../../../api/voiceOrders";
import type { Addon } from "../../../../../api/menu";
import {
  addonOptionPrice,
  addonOptionValue,
  type ResolvedVoiceRow,
} from "./voiceOrderResolution";

// „Слайдовете" на VoiceOrderModal — ЧИСТО презентационни компоненти, по един
// за всяко тяло на модала (слушане/парсване/грешка/избор/преглед). Тук НЯМА
// модали, state и заявки: всичко идва наготово през props, а кликовете само
// викат подадените callbacks — решенията се взимат горе във VoiceOrderModal.
// Отделени са в собствен файл единствено за размер (правилото ≤500 реда).

const formatElapsed = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Стъпка „слушане" — push-to-talk: кръгъл бутон, който записва САМО докато е
    задържан. Pointer events покриват мишка И touch; pointer capture гарантира,
    че пускането се хваща и извън бутона, а pointercancel (напр. системен жест
    или диалогът за разрешение на микрофона) също спира. Докато е задържан —
    пулсираща анимация + брояч на секундите (текстът идва чак след пускането,
    защото речта се разпознава на сървъра). */
export const ListeningStep = ({
  recording,
  elapsedSeconds,
  onHoldStart,
  onHoldEnd,
}: {
  recording: boolean;
  elapsedSeconds: number;
  onHoldStart: () => void;
  onHoldEnd: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 bg-pos-card px-6 py-10">
      {/* Кръглият push-to-talk бутон: touch-none спира скрола/жестовете при
          задържане на touch, select-none — селекцията при дълъг натиск,
          -webkit-touch-callout — iOS „балончето" при дълъг натиск,
          onContextMenu — контекстното меню на long-press. */}
      <button
        type="button"
        aria-label={t("ordering.voiceHold")}
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          onHoldStart();
        }}
        onPointerUp={onHoldEnd}
        onPointerCancel={onHoldEnd}
        onContextMenu={(event) => event.preventDefault()}
        className="relative grid h-24 w-24 cursor-pointer touch-none select-none place-items-center rounded-full [-webkit-touch-callout:none]"
      >
        {recording && (
          <span className="absolute inset-0 animate-ping rounded-full bg-pos-accent/20" />
        )}
        <span
          className={`relative grid h-20 w-20 place-items-center rounded-full text-[30px] transition-all ${
            recording
              ? "scale-110 bg-pos-accent text-white shadow-[0_0_0_8px_rgba(201,124,44,0.15)]"
              : "border border-pos-ctl bg-pos-panel text-pos-accent"
          }`}
        >
          <AudioOutlined />
        </span>
      </button>
      <div className="text-base font-extrabold text-pos-ink">
        {recording ? t("ordering.voiceListening") : t("ordering.voiceHold")}
      </div>
      <div className="min-h-[44px] px-2 text-center text-[14px] font-medium text-pos-body">
        {recording ? (
          <span className="font-mono text-[18px] font-bold tabular-nums text-pos-accent">
            {formatElapsed(elapsedSeconds)}
          </span>
        ) : (
          <span className="text-pos-muted">
            {t("ordering.voiceListeningHint")}
          </span>
        )}
      </div>
    </div>
  );
};

/** Стъпка „в обработка": спинер, докато тече заявка към BE — транскрипция
    (transcript = null, текстът още не е известен) или парсване (показва
    финалния транскрипт, който е заминал към модела). */
export const ParsingStep = ({
  transcript,
}: {
  transcript: string | null;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 bg-pos-card px-6 py-10">
      <LoadingOutlined className="text-[34px] text-pos-accent" />
      <div className="text-base font-extrabold text-pos-ink">
        {transcript === null
          ? t("ordering.voiceTranscribing")
          : t("ordering.voiceProcessing")}
      </div>
      <div className="min-h-[20px] px-2 text-center text-[13.5px] font-medium italic text-pos-muted">
        {transcript === null ? "" : `„${transcript}“`}
      </div>
    </div>
  );
};

/** Стъпка „грешка": тишина, отказан микрофон, неподдържан браузър или
    неуспешно парсване — текстът идва решен от модала (errorText). */
export const ErrorStep = ({ text }: { text: string }) => (
  <div className="flex flex-col items-center gap-4 bg-pos-card px-6 py-10">
    <WarningOutlined className="text-[34px] text-pos-danger-ink" />
    <div className="px-2 text-center text-[14.5px] font-semibold text-pos-ink2">
      {text}
    </div>
  </div>
);

// Ред-бутон за избор при двусмислие (продукт/модификатор/опция на добавка).
// Не пази нищо — „избраност" идва отвън (chosen), кликът само докладва нагоре.
export const ChoiceRow = ({
  chosen,
  label,
  priceText,
  onClick,
}: {
  chosen: boolean;
  label: string;
  priceText: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex cursor-pointer items-center justify-between rounded-[10px] border px-3.5 py-2.5 text-left text-[14px] font-semibold transition-colors ${
      chosen
        ? "border-pos-accent bg-pos-marked text-pos-ink"
        : "border-pos-ctl bg-pos-card text-pos-ink2 hover:bg-pos-panel"
    }`}
  >
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {priceText && (
      <span className="ml-3 shrink-0 font-pos-mono text-sm font-semibold text-pos-ink2">
        {priceText}
      </span>
    )}
  </button>
);

interface ChooseStepProps {
  ambiguousProducts: { item: VoiceOrderItemOut; index: number }[];
  rows: ResolvedVoiceRow[];
  chosenProductIds: Record<number, number>;
  chosenModifierIds: Record<string, number>;
  chosenAddonOptions: Record<string, string>;
  onChooseProduct: (rowIndex: number, productId: number) => void;
  onChooseModifier: (choiceKey: string, modifierId: number) => void;
  onChooseAddonOption: (choiceKey: string, value: string) => void;
  productName: (productId: number) => string;
  modifierName: (modifierId: number) => string;
  addonName: (addonId: number) => string;
  addonById: Map<number, Addon>;
  fmtPrice: (value: number) => string;
  itemPrice: (productId: number) => number | null;
}

/** Стъпка „избор" при двусмислие — три вида карти една след друга:
    1) продукти („кола" → 3 вида кола) — от ambiguousProducts;
    2) модификатори („да е малка" → няколко кандидата) — от
       row.pendingModifierChoices (появяват се ЧАК след като продуктът на
       реда е ясен, защото кандидатите се стесняват до неговите);
    3) опции на добавки („с кетчуп" → няколко групи/опции) — от
       row.pendingAddonChoices, по същото правило.
    Всеки клик отива в модала (setChosen*), rows се преизчислява и щом не
    остане нищо висящо, ефектът там прескача автоматично към прегледа. */
export const ChooseStep = ({
  ambiguousProducts,
  rows,
  chosenProductIds,
  chosenModifierIds,
  chosenAddonOptions,
  onChooseProduct,
  onChooseModifier,
  onChooseAddonOption,
  productName,
  modifierName,
  addonName,
  addonById,
  fmtPrice,
  itemPrice,
}: ChooseStepProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex max-h-[55vh] flex-col gap-4 overflow-y-auto bg-pos-card px-5 py-4">
      <div className="flex items-center gap-2 text-[13px] font-medium text-pos-muted">
        <QuestionCircleOutlined className="text-pos-accent" />
        {t("ordering.voiceChooseHint")}
      </div>

      {/* Двусмислени продукти */}
      {ambiguousProducts.map(({ item, index }) => (
        <div
          key={`p-${index}`}
          className="rounded-[14px] border border-pos-line3 bg-pos-panel p-3"
        >
          <div className="mb-2 text-[14px] font-bold text-pos-ink">
            <span className="font-pos-mono">{item.quantity} ×</span>{" "}
            „{item.spokenName}“
          </div>
          <div className="flex flex-col gap-1.5">
            {item.productIds.map((productId) => {
              const price = itemPrice(productId);
              return (
                <ChoiceRow
                  key={productId}
                  chosen={chosenProductIds[index] === productId}
                  label={productName(productId)}
                  priceText={price != null ? fmtPrice(price) : ""}
                  onClick={() => onChooseProduct(index, productId)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Двусмислени модификатори („да е малка") за вече ясните продукти */}
      {rows.flatMap((row) =>
        row.pendingModifierChoices.map((pending) => (
          <div
            key={pending.choiceKey}
            className="rounded-[14px] border border-pos-line3 bg-pos-panel p-3"
          >
            <div className="mb-2 text-[14px] font-bold text-pos-ink">
              {row.menuItem ? productName(row.menuItem.id) : row.spokenName}
              <span className="font-medium text-pos-muted">
                {" "}
                — „{pending.spokenText}“
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              {pending.candidateIds.map((modifierId) => (
                <ChoiceRow
                  key={modifierId}
                  chosen={chosenModifierIds[pending.choiceKey] === modifierId}
                  label={modifierName(modifierId)}
                  priceText=""
                  onClick={() => onChooseModifier(pending.choiceKey, modifierId)}
                />
              ))}
            </div>
          </div>
        )),
      )}

      {/* Двусмислени добавки („с кетчуп" при няколко опции/групи) */}
      {rows.flatMap((row) =>
        row.pendingAddonChoices.map((pending) => (
          <div
            key={pending.choiceKey}
            className="rounded-[14px] border border-pos-line3 bg-pos-panel p-3"
          >
            <div className="mb-2 text-[14px] font-bold text-pos-ink">
              {row.menuItem ? productName(row.menuItem.id) : row.spokenName}
              <span className="font-medium text-pos-muted">
                {" "}
                — „{pending.spokenText}“
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              {pending.candidates.map((option) => {
                const value = addonOptionValue(option.addonId, option.itemId);
                const addon = addonById.get(option.addonId);
                const price = addon
                  ? addonOptionPrice(addon, option.itemId)
                  : 0;
                return (
                  <ChoiceRow
                    key={value}
                    chosen={chosenAddonOptions[pending.choiceKey] === value}
                    label={`${productName(option.itemId)} (${addonName(option.addonId)})`}
                    priceText={price > 0 ? `+${price.toFixed(2)}` : ""}
                    onClick={() => onChooseAddonOption(pending.choiceKey, value)}
                  />
                );
              })}
            </div>
          </div>
        )),
      )}
    </div>
  );
};

/** Детайлите на ред за прегледа (смята ги модалът в rowDetails). */
export interface VoiceRowDetails {
  addons: { name: string; qty: number; price: number }[];
  modifiers: string[];
  comment: string;
}

/** Количество без излишни нули (3 → "3", 0.5 → "0.5") — както в кошницата. */
const formatQuantity = (quantity: number): string => {
  const rounded = Math.round(quantity * 10) / 10;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
};

interface PreviewStepProps {
  rows: ResolvedVoiceRow[];
  /** РЕДАКТИРУЕМОТО име на маса — предпопълнено с чутото; празно = не е
      разпозната маса и потребителят трябва да я напише. */
  tableNameInput: string;
  onTableNameChange: (value: string) => void;
  /** Enter В ПОЛЕТО на масата = „Поръчай" (глобалният Enter handler прескача
      input-ите, затова полето си вика submit-а само̀ — както коментарът в
      кошницата). Гейтовете на handleConfirm важат. */
  onSubmit: () => void;
  isExistingBill: boolean;
  orderTotal: number;
  productName: (productId: number) => string;
  rowDetails: (row: ResolvedVoiceRow) => VoiceRowDetails;
  fmtPrice: (value: number) => string;
  onConfigureRow: (rowIndex: number) => void;
}

/** Стъпка „преглед" — последната преди „Поръчай":
    - картата на масата: нова или допоръчка към отворена (isExistingBill);
      без разпозната маса → червено предупреждение (и „Поръчай" е блокиран);
    - редовете: количество × продукт + сума; под реда — избраните
      модификатори/добавки/коментар (rowDetailsText); моливчето отваря
      „Промени" за реда; ред с непокрити задължителни (needsConfig) е
      маркиран и носи червения бутон „Изберете задължителните добавки";
    - неразпознат продукт → зачертан, „няма съвпадение в менюто", не се
      поръчва;
    - „Общо" от orderTotal (само валидните редове). */
export const PreviewStep = ({
  rows,
  tableNameInput,
  onTableNameChange,
  onSubmit,
  isExistingBill,
  orderTotal,
  productName,
  rowDetails,
  fmtPrice,
  onConfigureRow,
}: PreviewStepProps) => {
  const { t } = useTranslation();
  const hasTableName = tableNameInput.trim().length > 0;
  return (
    <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto bg-pos-card px-5 py-4">
      {/* Маса — РЕДАКТИРУЕМА: чутото е предпопълнено, но може да се смени
          (заета маса от друг оператор) или да се напише от нулата (не е
          казана). Празно поле = червен бордер + блокиран „Поръчай". */}
      <div
        className={`flex items-center gap-2.5 rounded-[12px] border px-3.5 py-2 ${
          hasTableName
            ? "border-pos-line3 bg-pos-panel"
            : "border-pos-danger-brd bg-pos-danger-bg"
        }`}
      >
        <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-sec-head">
          {t("ordering.voiceTable")}
        </span>
        <input
          value={tableNameInput}
          onChange={(event) => onTableNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            onSubmit();
          }}
          placeholder={t("ordering.voiceTablePlaceholder")}
          maxLength={40}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-[17px] font-extrabold text-pos-ink outline-none placeholder:text-[13px] placeholder:font-semibold placeholder:text-pos-danger-ink"
        />
        <span
          className={`ml-auto shrink-0 text-[12px] font-semibold ${
            hasTableName ? "text-pos-muted" : "text-pos-danger-ink"
          }`}
        >
          {hasTableName ? (
            isExistingBill ? (
              t("ordering.voiceTableExisting")
            ) : (
              t("ordering.voiceTableNew")
            )
          ) : (
            <WarningOutlined />
          )}
        </span>
      </div>

      {/* Разпознати продукти */}
      <div className="overflow-hidden rounded-[14px] border border-pos-line3 bg-pos-card">
        {rows.map((row) => {
          const details = row.menuItem ? rowDetails(row) : null;
          return row.menuItem && details ? (
            <div
              key={row.index}
              className={`flex flex-col gap-1.5 border-b border-pos-rowline px-3.5 py-2.5 last:border-b-0 ${
                row.needsConfig ? "bg-pos-marked" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="font-pos-mono text-[14px] font-semibold text-pos-accent-ink">
                  {row.config.quantity} ×
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-pos-ink">
                  {productName(row.menuItem.id)}
                </span>
                <span className="shrink-0 font-pos-mono text-sm font-semibold text-pos-ink2">
                  {fmtPrice(row.lineTotal)}
                </span>
                {/* Редакция на реда — отваря „Промени" с гласовия избор */}
                <button
                  type="button"
                  aria-label="edit"
                  onClick={() => onConfigureRow(row.index)}
                  className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-[9px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
                >
                  <EditOutlined className="text-[13px]" />
                </button>
              </div>

              {/* Добавките — chips както в кошницата/детайла на маса */}
              {details.addons.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {details.addons.map((addonChip, chipIndex) => (
                    <span
                      key={chipIndex}
                      className="inline-flex items-center gap-1 rounded-[7px] border border-pos-addon-brd bg-pos-addon-bg px-1.5 py-0.5 text-[11px] text-pos-addon-ink"
                    >
                      <span className="text-pos-disc-ink">＋</span>
                      {addonChip.name}
                      <span className="font-pos-mono">
                        {formatQuantity(addonChip.qty)}×
                        {addonChip.price.toFixed(2)}
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {/* Модификаторите — червен курсивен ред, пренася се (не се реже) */}
              {details.modifiers.length > 0 && (
                <div className="min-w-0 break-words text-[12.5px] font-semibold italic text-pos-danger-ink">
                  {details.modifiers.join(" · ")}
                </div>
              )}

              {/* Коментарът към продукта — червен ред с иконка, като в кошницата */}
              {details.comment && (
                <div className="flex min-w-0 items-start gap-1.5 text-[13px] font-semibold italic text-pos-danger-ink">
                  <CommentOutlined className="mt-1 shrink-0 text-xs" />
                  <span className="min-w-0 break-words">{details.comment}</span>
                </div>
              )}

              {row.needsConfig && (
                <button
                  type="button"
                  onClick={() => onConfigureRow(row.index)}
                  className="ml-9 mt-0.5 inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-[9px] border border-pos-danger-brd bg-pos-danger-bg px-2.5 py-1 text-[12px] font-semibold text-pos-danger-ink"
                >
                  <WarningOutlined />
                  {t("ordering.voiceNeedsConfig")}
                </button>
              )}
            </div>
          ) : (
            <div
              key={row.index}
              className="flex items-center gap-3 border-b border-pos-rowline px-3.5 py-2.5 last:border-b-0"
            >
              <span className="font-pos-mono text-[14px] font-semibold text-pos-muted">
                {row.config.quantity} ×
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-pos-muted line-through">
                „{row.spokenName}“
              </span>
              <span className="shrink-0 text-[12px] font-semibold text-pos-danger-ink">
                {t("ordering.voiceNoMatch")}
              </span>
            </div>
          );
        })}
      </div>

      {/* Общо */}
      <div className="flex items-baseline justify-between px-1">
        <span className="text-sm font-semibold text-pos-soft2">
          {t("common.total")}
        </span>
        <span className="font-pos-mono text-[22px] font-bold text-pos-ink">
          {fmtPrice(orderTotal)}
        </span>
      </div>
    </div>
  );
};
